import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FaArrowLeft,
  FaTrash,
  FaShoppingCart,
  FaSearch,
  FaTag,
  FaCreditCard,
  FaCheckCircle,
} from "react-icons/fa";
import {
  Container,
  CTAButton,
  PageHeader,
  CartSection,
  CartGrid,
  CartItems,
  CartHeader,
  ClearCartButton,
  CartItem,
  ItemImage,
  ItemDetails,
  ItemName,
  ItemDescription,
  ItemControls,
  ItemPrice,
  RemoveButton,
  EmptyCart,
  CartSummary,
  SummaryHeader,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  PromoCode,
  PromoInput,
  PromoButton,
  CheckoutButton,
  ContinueShoppingLink,
  SuccessMessage,
  PaymentMethods,
} from "./CartPage.styles";
import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";
import { useNavigate } from "react-router-dom";
import {
  selectCartItems,
  selectCartTotalItems,
  selectCartSubtotal,
  selectCartDiscount,
  selectCartTotal,
  selectAppliedPromo,
  selectCartLoading,
  selectCartIsEmpty,
  setAppliedPromo,
  clearAppliedPromo,
} from "../../store/slices/cart/slice";
import {
  loadCart,
  removeFromCart,
  clearCart,
  applyPromoCode,
} from "../../store/slices/cart/asyncThunks";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import Modal from "../../components/utils/Modal";
import { priceFormatter } from "../../utils/utils";
import { ReactComponent as PaymentAccept } from "../../assets/icons/banka.svg";
import { BASE_URL } from "../../api";

const CartPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);

  // Redux selectors
  const cartItems = useSelector(selectCartItems);
  const totalItems = useSelector(selectCartTotalItems);
  const subtotal = useSelector(selectCartSubtotal);
  const discount = useSelector(selectCartDiscount);
  const total = useSelector(selectCartTotal);
  const appliedPromo = useSelector(selectAppliedPromo);
  const cartLoading = useSelector(selectCartLoading);
  const isCartEmpty = useSelector(selectCartIsEmpty);
  const isLoggedIn = useSelector(selectLoggedIn);

  // Local state
  const [promoInput, setPromoInput] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [removingItems, setRemovingItems] = useState(new Set());

  // Load cart on component mount
  useEffect(() => {
    dispatch(loadCart());
  }, [dispatch]);

  // Handle success message display
  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  // Handle item removal with animation
  const handleRemoveItem = (courseId) => {
    setRemovingItems((prev) => new Set([...prev, courseId]));
    setTimeout(() => {
      dispatch(removeFromCart(courseId));
      setRemovingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    }, 300);
  };

  // Handle clear cart
  const handleClearCart = () => {
    setModalOpen(true);
  };

  // Handle promo code application
  const handleApplyPromoCode = () => {
    const code = promoInput.trim().toUpperCase();
    if (code) {
      dispatch(applyPromoCode(code))
        .unwrap()
        .then((promo) => {
          dispatch(setAppliedPromo(promo));
          setPromoInput("");
        })
        .catch((error) => {
          // Error je već prikazan u toast iz asyncThunk
          console.error("Promo code error:", error);
        });
    }
  };

  // Handle remove promo code
  const handleRemovePromoCode = () => {
    dispatch(clearAppliedPromo());
    showSuccessMessage("Promo kod je uklonjen");
  };

  // Handle checkout
  const proceedToCheckout = () => {
    if (isCartEmpty) return;

    // Check if user is logged in
    if (!isLoggedIn) {
      navigate("/pristup?redirect=/korpa");
      return;
    }

    const checkoutData = {
      items: cartItems,
      appliedPromo: appliedPromo,
      subtotal: subtotal,
      discount: discount,
      total: total,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem("checkoutData", JSON.stringify(checkoutData));

    navigate("/placanje");
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  // Navigate helper
  const onNavigate = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  return (
    <MainContainer>
      <SEO title="Korpa" path="/korpa" noindex />

      {/* Page Header */}
      <PageHeader>
        <Container>
          <h1>Vaša Korpa</h1>
          <p>
            Odličan izbor! Ovde možete da pregledate sve kurseve koje ste
            odabrali i prilagodite vašu porudžbinu pre plaćanja.
          </p>
        </Container>
      </PageHeader>
      {/* Cart Section */}
      <CartSection>
        <Container>
          <SuccessMessage $show={showSuccess}>
            <FaCheckCircle />
            <span>{successMessage}</span>
          </SuccessMessage>

          <CartGrid $isCartEmpty={isCartEmpty}>
            {/* Cart Items */}
            <CartItems>
              <CartHeader>
                <h2>Kursevi u Korpi ({totalItems})</h2>
                {!isCartEmpty && (
                  <ClearCartButton
                    onClick={handleClearCart}
                    disabled={cartLoading}
                  >
                    <FaTrash />
                    Obriši Sve
                  </ClearCartButton>
                )}
              </CartHeader>

              {isCartEmpty ? (
                <EmptyCart>
                  <FaShoppingCart size={64} />
                  <h3>Vaša korpa je prazna</h3>
                  <p>
                    Još uvek niste dodali nijedan kurs u korpu.
                    <br />
                    Istražite našu kolekciju i pronađite savršen kurs za vas!
                  </p>
                  <CTAButton onClick={() => onNavigate("/kursevi")}>
                    <FaSearch />
                    Istražite Kurseve
                  </CTAButton>
                </EmptyCart>
              ) : (
                cartItems.map((item, i) => (
                  <CartItem
                    key={i}
                    $removing={removingItems.has(item.courseId)}
                  >
                    <ItemImage
                      src={`${BASE_URL}/uploads/courses-thumbnails/${item.thumbnailImage}`}
                      alt={`${item.title} thumbnail`}
                    />
                    <ItemDetails>
                      <ItemName>{item.title}</ItemName>
                      <ItemDescription>{item.description}</ItemDescription>
                    </ItemDetails>
                    <ItemControls>
                      <ItemPrice>
                        {priceFormatter(item.price)} {item.currency}
                      </ItemPrice>
                      <RemoveButton
                        onClick={() => handleRemoveItem(item.courseId)}
                        disabled={cartLoading}
                      >
                        <FaTrash />
                      </RemoveButton>
                    </ItemControls>
                  </CartItem>
                ))
              )}
            </CartItems>

            {/* Cart Summary - Prikaži samo ako korpa nije prazna */}
            {!isCartEmpty && (
              <CartSummary>
                <SummaryHeader>Pregled Porudžbine</SummaryHeader>

                <SummaryRow>
                  <SummaryLabel>Ukupno Kurseva:</SummaryLabel>
                  <SummaryValue>{totalItems}</SummaryValue>
                </SummaryRow>

                <SummaryRow>
                  <SummaryLabel>Međuzbir:</SummaryLabel>
                  <SummaryValue>{priceFormatter(subtotal)} RSD</SummaryValue>
                </SummaryRow>

                {appliedPromo && (
                  <SummaryRow>
                    <SummaryLabel>Popust ({appliedPromo.name}):</SummaryLabel>
                    <SummaryValue className="discount">
                      -{priceFormatter(discount)} RSD
                    </SummaryValue>
                  </SummaryRow>
                )}

                <SummaryRow className="total-row">
                  <SummaryLabel className="total">UKUPNO:</SummaryLabel>
                  <SummaryValue className="total">
                    {priceFormatter(total)} RSD
                  </SummaryValue>
                </SummaryRow>

                {/* Promo Code Section */}
                <PromoCode>
                  <PromoInput
                    type="text"
                    placeholder="Unesite promo kod"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleApplyPromoCode()
                    }
                    disabled={!!appliedPromo || cartLoading}
                  />
                  <PromoButton
                    onClick={
                      appliedPromo
                        ? handleRemovePromoCode
                        : handleApplyPromoCode
                    }
                    disabled={
                      (!appliedPromo && !promoInput.trim()) || cartLoading
                    }
                  >
                    {appliedPromo ? (
                      <>
                        <FaTrash />
                        Ukloni Kod
                      </>
                    ) : (
                      <>
                        <FaTag />
                        Primeni Kod
                      </>
                    )}
                  </PromoButton>
                </PromoCode>

                <CheckoutButton
                  onClick={proceedToCheckout}
                  disabled={isCartEmpty || cartLoading}
                >
                  <FaCreditCard />
                  {cartLoading ? "Učitava..." : "Nastavi ka Plaćanju"}
                </CheckoutButton>

                <ContinueShoppingLink onClick={() => onNavigate("/kursevi")}>
                  <FaArrowLeft />
                  Nastavi Kupovinu
                </ContinueShoppingLink>

                <PaymentMethods>
                  <p>Sigurno Plaćanje</p>
                  <PaymentAccept height={30} />
                </PaymentMethods>
              </CartSummary>
            )}
          </CartGrid>
        </Container>
      </CartSection>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Isprazni korpu?"
        subtitle="Ova akcija će ukloniti sve proizvode iz korpe. Ovu radnju nije moguće opozvati."
        leftButtonText="Odustani"
        rightButtonText="Potvrdi"
        onRightButtonClick={() => {
          dispatch(clearCart());
          dispatch(clearAppliedPromo());
        }}
      />
    </MainContainer>
  );
};

export default CartPage;
